import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AssistantMessageRead } from '../../types/api';
import { claimClientActions, resolveClientAction } from './clientActions';

/**
 * Performs the client actions on replies as they arrive: once per message id,
 * only for a reply to a message this tab sent (`claimClientActions`), and only
 * after each action clears its own check. Mounted wherever a thread is live
 * (the bubble, even closed, and the full page), so a reply that lands while the
 * panel is shut still does what was asked.
 *
 * `onPerformed` fires after a navigation, so a full-screen panel on a phone can
 * step aside and show the page it opened.
 */
export function useClientActionRunner(
  messages: readonly AssistantMessageRead[],
  onPerformed?: () => void,
): void {
  const navigate = useNavigate();
  const performedRef = useRef(onPerformed);

  useEffect(() => {
    performedRef.current = onPerformed;
  }, [onPerformed]);

  useEffect(() => {
    for (const message of messages) {
      if (!claimClientActions(message)) continue;
      // Several actions on one reply would each pull the screen elsewhere; the
      // last allowed one is where the user ends up, so only that one runs.
      const allowed = message.client_actions
        .map(resolveClientAction)
        .filter((resolved) => resolved !== null);
      const last = allowed[allowed.length - 1];
      if (last === undefined) continue;
      void navigate(last.to, { state: last.state });
      performedRef.current?.();
    }
  }, [messages, navigate]);
}
